import { Dispatch, Fragment, SetStateAction, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import useDeletePage from "../../api/api-hooks/pages/useDeletePage";
import useWorkspace from "../../api/api-hooks/workspaces/useWorkspace";
import useWorkspacePages from "../../api/api-hooks/workspaces/useWorkspacePages";
import useWorkspacePermissions from "../../hooks/useWorkspacePermissions";
import useToggle from "../../hooks/useToggle";
import AddPageDrawer from "../Drawers/AddPageDrawer";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import AppPageHeader from "../Headers/AppPageHeader";
import SkeletonLoader from "../../elements/SkeletonLoader";
import AppEmptyState from "../../elements/AppEmptyState";
import useDeleteItem from "../../api/api-hooks/workspaces/sidebar/useDeleteItem";
import AppItemCard from "../Modules/AppItemCard";
import IconButton from "../../elements/IconButton";
import { Page } from "../../types/GlobalInterfaces";
import PageSettingsDrawer from "../Drawers/PageSettingsDrawer";
import PopoverDropdownMenu from "../Menus/PopoverDropdownMenu";
import DropdownOptionWithIcon from "../Menus/DropdownOptionWithIcon";
import useDuplicateFolder from "../../api/api-hooks/folders/useDuplicateFolder";
import useUpdateItem from "../../api/api-hooks/workspaces/sidebar/useUpdateItem";
import RenameModal from "../Sidebar/RenameModal";
import DotsOptionsMenu from "../Menus/DotsOptionsMenu";
import Constants from "../../utils/Constants";

const LoadingPages = () => (
  <>
    <SkeletonLoader />
    <SkeletonLoader />
    <SkeletonLoader />
    <SkeletonLoader />
  </>
);

const PagesList = () => {
  const { pagesLoading } = useWorkspacePages();
  const { workspace } = useWorkspace();
  const { pages } = useWorkspacePages();
  const { deletePage, deletePageID } = useDeletePage();
  const [pageModal, togglePageModal] = useToggle();
  const { tenantID } = useParams();
  const { wPermissions } = useWorkspacePermissions();
  const { cPermissions } = useCloudPermissions();
  const { deleteItem } = useDeleteItem();
  const [pageSettings, setPageSettings] = useState<Page | undefined | null>(
    null
  );
  const [addPage, setAddPage] = useState<string | null>(null);
  const [renameItem, setRenameItem] = useState<any>(null);
  const { updateItem } = useUpdateItem();

  const navigate = useNavigate();

  const handlePageDelete = (pageID: string) => {
    deletePage({ pageID });
  };

  const sidebarItems = useMemo(() => {
    return workspace.Sidebar?.flatMap((i) => {
      if (!["Group", "PageLink"].includes(i?.Type)) return [];

      if (i?.Type === "Group") {
        i.Items = i?.Items.flatMap((subI: any) => {
          if (subI.Type === "PageLink") return [subI];

          return [];
        });
      }

      return [i];
    });
  }, [workspace?.Sidebar, pages]);

  const NoPages = () => (
    <AppEmptyState
      title="All Pages"
      description={` ${workspace?.Name}  doesn't have any pages yet. Get
          started by adding the first one.`}
      buttonTitle="Add Page"
      onClick={togglePageModal}
      hideIcon={true}
      buttonDisabled={
        !wPermissions.workspace_create_page && !cPermissions.cloud_create_page
      }
    />
  );

  const SupportalPage = (
    <AppEmptyState
      title="No Pages Available"
      description="Pages are not available for this workspace"
      buttonTitle="Add Page"
      onClick={togglePageModal}
      hideIcon={true}
      buttonDisabled={true}
    />
  );

  const FolderPages = ({ items }: { items: any[] }) => {
    return (
      <>
        {items?.length &&
          items.map((i) => {
            return (
              <Fragment key={`folder-pages-${i?.id}`}>
                <AppItemCard
                  rowClassName="ml-4"
                  title={i?.Title}
                  icon="mdi mdi-file-table-box-outline"
                  onClick={() =>
                    navigate(
                      `/clouds/${tenantID}/workspaces/${workspace.id}/pages/${i?.PageID}/views/None`
                    )
                  }
                  options={
                    <>
                      <IconButton
                        icon="mdi mdi-cog"
                        tooltipText="Page Settings"
                        onClick={() => {
                          setPageSettings(pages.find((p) => p.id === i.PageID));
                        }}
                      />
                      <IconButton
                        icon="mdi mdi-close"
                        color="danger"
                        tooltipText="Remove Page"
                        onClick={() => {
                          handlePageDelete(i?.PageID);
                        }}
                      />
                    </>
                  }
                />
              </Fragment>
            );
          })}
      </>
    );
  };

  if (tenantID === Constants.SUP_CLOUD_ID) {
    return SupportalPage;
  }

  return (
    <>
      {!pagesLoading && pages?.length === 0 ? (
        <NoPages />
      ) : (
        <div>
          <AppPageHeader
            title="All Pages"
            description="A list of all the pages in this workspace"
            action={togglePageModal}
            actionButtonName="Add"
            disabled={
              !wPermissions.workspace_create_page &&
              !cPermissions.cloud_create_page
            }
            size="sm"
          />
          <hr className="my-3" />
          {pagesLoading ? (
            <LoadingPages />
          ) : (
            sidebarItems.map((item, i) => {
              return (
                <Fragment key={item.id}>
                  <AppItemCard
                    title={item?.Title}
                    openIcon={"mdi mdi-folder-open-outline"}
                    icon={
                      item?.Type === "PageLink"
                        ? "mdi mdi-file-table-box-outline"
                        : "mdi mdi-folder-outline"
                    }
                    onClick={
                      item?.Type === "PageLink"
                        ? () =>
                            navigate(
                              `/clouds/${tenantID}/workspaces/${workspace.id}/pages/${item.PageID}/views/None`
                            )
                        : undefined
                    }
                    options={
                      <>
                        {item?.Type === "PageLink" ? (
                          <IconButton
                            icon="mdi mdi-cog"
                            tooltipText="Page Settings"
                            onClick={() => {
                              setPageSettings(
                                pages.find((p) => p.id === item.PageID)
                              );
                            }}
                          />
                        ) : (
                          <div className="pr-3">
                            <DotsOptionsMenu
                              id={item.id}
                              dotsSize={20}
                              placement="autoVerticalEnd"
                              options={
                                <FolderMenu
                                  item={item}
                                  cPermissions={cPermissions}
                                  wPermissions={wPermissions}
                                  setRenameItem={setRenameItem}
                                  setAddPage={setAddPage}
                                />
                              }
                            />
                          </div>
                        )}
                        <IconButton
                          icon="mdi mdi-close"
                          color="danger"
                          tooltipText={
                            item?.Type === "PageLink"
                              ? "Remove Page"
                              : "Remove Folder"
                          }
                          onClick={() => {
                            item?.Type === "PageLink"
                              ? handlePageDelete(item?.PageID)
                              : deleteItem(item?.id);
                          }}
                        />
                      </>
                    }
                    children={
                      item?.Type === "Group" && item?.Items?.length ? (
                        <FolderPages items={item?.Items} />
                      ) : undefined
                    }
                  />
                </Fragment>
              );
            })
          )}
        </div>
      )}
      <AddPageDrawer open={pageModal} handleClose={togglePageModal} />
      <PageSettingsDrawer
        open={!!pageSettings}
        onClose={() => {
          setPageSettings(null);
        }}
        page={pageSettings!}
      />
      <RenameModal
        open={!!renameItem}
        handleClose={() => {
          setRenameItem(null);
        }}
        item={renameItem}
        onSave={(newName: string, itemId: string) => {
          updateItem({ newName, itemId });
        }}
      />
      <AddPageDrawer
        open={addPage !== null}
        handleClose={() => setAddPage(null)}
        folderID={addPage ?? ""}
      />
    </>
  );
};

const FolderMenu = ({
  item,
  wPermissions,
  cPermissions,
  setRenameItem,
  setAddPage,
}: {
  item: any;
  cPermissions: any;
  wPermissions: any;
  setRenameItem: Dispatch<SetStateAction<any>>;
  setAddPage: Dispatch<SetStateAction<string | null>>;
}) => {
  const { duplicateFolder } = useDuplicateFolder();

  return (
    <PopoverDropdownMenu>
      <DropdownOptionWithIcon
        name="Add Page To Folder"
        icon="mdi mdi-file-table-box-outline"
        onClick={() => {
          setAddPage(item.id);
        }}
      />
      <DropdownOptionWithIcon
        name="Rename Folder"
        onClick={() => {
          setRenameItem({ ...item, data: { Title: item?.Title } });
        }}
        icon="mdi mdi-pencil-outline"
        disabled={
          !cPermissions.cloud_duplicate_folder ||
          !wPermissions.workspace_duplicate_folder
        }
      />
      <DropdownOptionWithIcon
        name="Duplicate Folder"
        onClick={() => duplicateFolder(item.id)}
        icon="mdi mdi-content-copy"
        disabled={
          !cPermissions.cloud_duplicate_folder ||
          !wPermissions.workspace_duplicate_folder
        }
      />
    </PopoverDropdownMenu>
  );
};

export default PagesList;
